/**
 * @module IssShared
 * @description Shared building blocks for lane/ISS device UI, extracted to remove
 * duplication across FleetManager (IssControls, IssModulesPanel) and
 * FleetDetailPanel:
 *   - ModulesTable: the active-module-set comparison table (legend + rows with
 *     match/mismatch indicators against the expected S3 module set).
 *   - useIssRestart: the ISS restart flow (confirm → onRestart → status message).
 */
import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import CopyButton from './CopyButton';

/**
 * Builds a markdown table of the module set for the Version-header copy icon:
 * a "| Module | Version |" header plus one row per module, pasteable into any
 * markdown surface (CR, wiki, doc). Cell values are pipe-escaped so a literal
 * "|" in a name or version cannot add a column and shift the table. Deliberately
 * excludes the "Matched with S3 versions" column — the header is asserted in the
 * unit test so that intent stays enforceable if a column is ever added to
 * ModulesTable.
 *
 * @param {Object<string,string>} modules - device module -> version map
 * @returns {string} a markdown table (header + divider + one row per module)
 */
function buildModulesMarkdown(modules) {
  const cell = (v) => String(v).replace(/\|/g, '\\|');
  return [
    '| Module | Version |',
    '| --- | --- |',
    ...Object.entries(modules || {}).map(([name, version]) => `| ${cell(name)} | ${cell(version)} |`),
  ].join('\n');
}

/**
 * Renders the active module set as a table, optionally comparing each module's
 * device version against the expected S3 module set (match / differs / n/a).
 *
 * @param {object} props
 * @param {Object<string,string>} props.modules - device module → version map
 * @param {Object<string,string>|null} [props.expectedModules] - expected S3 set
 * @param {string} [props.stage] - deploymentStage, used for the legend label
 * @param {string} [props.emptyMessage] - shown when modules is an empty object
 */
function ModulesTable({ modules, expectedModules, stage, emptyMessage = 'No modules found on this lane.' }) {
  if (!modules) return null;
  if (Object.keys(modules).length === 0) {
    return <div className="fleet-modules-empty">{emptyMessage}</div>;
  }

  const compareLabel = stage?.toLowerCase().includes('gamma') ? 'Gamma' : 'Beta';

  const allModulesText = buildModulesMarkdown(modules);

  return (
    <>
      {expectedModules && (
        <div className="fleet-modules-legend">
          Compared against latest {compareLabel} module set
          <span className="fleet-modules-legend-items">
            <span className="fleet-mod-match"><Check size={12} /> match</span>
            <span className="fleet-mod-mismatch"><X size={12} /> differs</span>
          </span>
        </div>
      )}
      <table className="fleet-modules-table">
        <thead>
          <tr>
            <th>Module</th>
            <th>Version<CopyButton value={allModulesText} label="Copy all modules and versions" /></th>
            {expectedModules && <th className="fleet-mod-status-col">Matched with S3 versions</th>}
          </tr>
        </thead>
        <tbody>
          {Object.entries(modules).map(([name, version]) => {
            const expectedVersion = expectedModules ? expectedModules[name] : undefined;
            const hasExpectation = expectedVersion !== undefined;
            const matched = hasExpectation && String(expectedVersion) === String(version);
            return (
              <tr key={name}>
                <td>{name}</td>
                <td>{version}</td>
                {expectedModules && (
                  <td className="fleet-mod-status">
                    {!hasExpectation ? (
                      <span className="fleet-mod-na" title="Not in expected module set">—</span>
                    ) : matched ? (
                      <span className="fleet-mod-match" title="Matches expected version">
                        <Check size={15} />
                      </span>
                    ) : (
                      <span className="fleet-mod-mismatch" title={`Expected ${expectedVersion}`}>
                        <X size={15} /> {expectedVersion}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/**
 * Encapsulates the ISS restart flow shared by every ISS control surface:
 * a confirm dialog, the onRestart call, and a transient status message.
 *
 * @param {(laneId: string) => Promise<void>} onRestart - performs the reboot
 * @param {object} iss - the ISS lane record ({ laneId, hardwareId })
 * @returns {{ restarting: boolean, message: {type: string, text: string}|null, restart: () => Promise<void> }}
 */
function useIssRestart(onRestart, iss) {
  const [restarting, setRestarting] = useState(false);
  const [message, setMessage] = useState(null);

  const restart = async () => {
    if (!onRestart || !iss?.laneId) return;
    const confirmed = window.confirm(
      `Restart ISS ${iss.hardwareId || iss.laneId}?\n\nThe ISS will reboot and be unavailable for a few minutes.`
    );
    if (!confirmed) return;
    setRestarting(true);
    setMessage(null);
    try {
      await onRestart(iss.laneId);
      setMessage({ type: 'info', text: 'ISS rebooting — this takes a few minutes.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to restart the ISS.' });
    } finally {
      setRestarting(false);
    }
  };

  return { restarting, message, restart };
}

/**
 * Fetches the active module set for a lane/ISS via the shared
 * /fleet/lanes/{laneId}/modules endpoint. Re-fetches when laneId, dsn, fleetApi,
 * or reloadKey change; skips (and clears) when laneId/dsn/fleetApi are absent.
 *
 * @param {string} laneId
 * @param {string} dsn - device DSN; without it there's nothing to reach via SSM
 * @param {(path: string, options?: object) => Promise<any>} fleetApi
 * @param {number} [reloadKey] - bump to force a manual re-fetch
 * @returns {{ modules, expectedModules, eleraVersion, osVersion, loading, error }}
 */
function useIssModules(laneId, dsn, fleetApi, reloadKey = 0) {
  const [modules, setModules] = useState(null);
  const [expectedModules, setExpectedModules] = useState(null);
  const [eleraVersion, setEleraVersion] = useState(null);
  const [osVersion, setOsVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!laneId || !dsn || !fleetApi) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setModules(null);
      setExpectedModules(null);
      setEleraVersion(null);
      setOsVersion(null);
      try {
        const data = await fleetApi(`/fleet/lanes/${laneId}/modules`);
        if (!cancelled) {
          setModules(data.modules);
          setExpectedModules(data.expectedModules || null);
          setEleraVersion(data.eleraVersion || null);
          setOsVersion(data.osVersion || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to fetch ISS modules');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [laneId, dsn, fleetApi, reloadKey]);

  return { modules, expectedModules, eleraVersion, osVersion, loading, error };
}

export { ModulesTable, useIssRestart, useIssModules, buildModulesMarkdown };
